import { CobaltBreadcrumb } from '@cae/cobalt-react/components/breadcrumb';
import { CobaltLink } from '@cae/cobalt-react/components/link';
import { useDocumentsFilter } from '@features/documents/contexts/documents-filter/useDocumentsFilter';
import * as React from 'react';

import type { DocumentsBreadcrumbProps } from './DocumentsBreadcrumb.types';

const DocumentsBreadcrumb = React.memo(
  ({
    rootLabel = 'Root',
  }: Readonly<DocumentsBreadcrumbProps>): React.ReactElement | null => {
    const { filters, navigateToBreadcrumb, navigateToRoot } =
      useDocumentsFilter();

    const handleRootClick = React.useCallback((): void => {
      navigateToRoot();
    }, [navigateToRoot]);

    const handleBreadcrumbClick = React.useCallback(
      (index: number): void => {
        const folder = filters.parentFolders[index];
        navigateToBreadcrumb(folder.id, index);
      },
      [filters.parentFolders, navigateToBreadcrumb]
    );

    if (filters.parentFolders.length === 0) {
      return null;
    }

    return (
      <CobaltBreadcrumb data-testid="documents-breadcrumb">
        <CobaltLink onClick={handleRootClick}>{rootLabel}</CobaltLink>
        {filters.parentFolders.map((folder, index) => {
          const isLast = index === filters.parentFolders.length - 1;
          return isLast ? (
            <span key={folder.id} aria-current="page">
              {folder.name}
            </span>
          ) : (
            <CobaltLink
              key={folder.id}
              onClick={() => handleBreadcrumbClick(index)}
            >
              {folder.name}
            </CobaltLink>
          );
        })}
      </CobaltBreadcrumb>
    );
  }
);

DocumentsBreadcrumb.displayName = 'DocumentsBreadcrumb';

export { DocumentsBreadcrumb };
